import React, { useState } from "react";
import { updateSystemExplanation } from "../../../../Services/SystemExplanationsApi";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  CircularProgress,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import InfoIcon from "@mui/icons-material/Info";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SaveIcon from "@mui/icons-material/Save";

const EditSystemExplanation = ({ explanationId, existingData, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: existingData?.title || "",
    description: existingData?.description || "",
    type: existingData?.type || "",
    is_active: existingData?.is_active || "1",
    image: null,
    version: existingData?.version || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, image: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== "") {
          formDataToSend.append(key, formData[key]);
        }
      });

      await updateSystemExplanation(explanationId, formDataToSend);
      alert("✅ تم تعديل شرح النظام بنجاح!");
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("❌ Error updating system explanation:", error);
      alert("حدث خطأ أثناء التعديل.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {/* العنوان والوصف */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#1976d2' }}>
                  <TitleIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  المعلومات الأساسية
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="عنوان الشرح"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    sx={{ bgcolor: 'white' }}
                    InputProps={{
                      startAdornment: <TitleIcon sx={{ mr: 1, color: '#1976d2' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="وصف الشرح"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    required
                    multiline
                    rows={6}
                    variant="outlined"
                    sx={{ bgcolor: 'white' }}
                    InputProps={{
                      startAdornment: <DescriptionIcon sx={{ mr: 1, color: '#1976d2', alignSelf: 'flex-start', mt: 1 }} />
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* النوع والحالة */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#2196f3' }}>
                  <CategoryIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  النوع
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <FormControl fullWidth required sx={{ bgcolor: 'white' }}>
                <InputLabel>نوع الشرح</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  label="نوع الشرح"
                >
                  <MenuItem value="">
                    <em>اختر النوع</em>
                  </MenuItem>
                  <MenuItem value="info">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="info" fontSize="small" />
                      معلومات (Info)
                    </Box>
                  </MenuItem>
                  <MenuItem value="warning">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="warning" fontSize="small" />
                      تحذير (Warning)
                    </Box>
                  </MenuItem>
                  <MenuItem value="error">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="error" fontSize="small" />
                      خطأ (Error)
                    </Box>
                  </MenuItem>
                  <MenuItem value="feature">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="success" fontSize="small" />
                      ميزة (Feature)
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#9c27b0' }}>
                  <ToggleOnIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                  الحالة
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <FormControl fullWidth required sx={{ bgcolor: 'white' }}>
                <InputLabel>حالة الشرح</InputLabel>
                <Select
                  name="is_active"
                  value={formData.is_active}
                  onChange={handleChange}
                  label="حالة الشرح"
                >
                  <MenuItem value="1">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="نشط" color="success" size="small" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="0">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="غير نشط" color="default" size="small" />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* الإصدار */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#4caf50' }}>
                  <InfoIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                  معلومات الإصدار
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TextField
                label="رقم الإصدار"
                name="version"
                value={formData.version}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                sx={{ bgcolor: 'white' }}
                placeholder="مثال: 1.0.0"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* تحميل الصورة */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#ff9800' }}>
                  <CloudUploadIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                  الصورة
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<CloudUploadIcon />}
                sx={{ 
                  py: 2,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  '&:hover': {
                    borderColor: '#f57c00',
                    bgcolor: '#fff3e0'
                  }
                }}
              >
                {formData.image ? 'تغيير الصورة' : 'تحميل صورة جديدة (اختياري)'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </Button>
              {formData.image && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Chip 
                    label={`✓ تم اختيار: ${formData.image.name}`}
                    color="success"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* زر التحديث */}
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              py: 2,
              fontWeight: 'bold',
              fontSize: '1.1rem',
              background: 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)',
                transform: 'scale(1.02)',
                transition: 'all 0.2s'
              },
              '&:disabled': {
                background: '#e0e0e0'
              }
            }}
          >
            {submitting ? 'جاري التحديث...' : 'حفظ التعديلات'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EditSystemExplanation;
